import {Component, EventEmitter, Type, ViewChild} from '@angular/core';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {firstValueFrom, Observable} from 'rxjs';
import {EDialogOpenMode} from '../../enums/dialog-open-mode.enum';
import {IBasePageComponent} from '../ibase-page/ibase-page.component';
import {
    ICRUModalConfigBase as IBaseCRUModalConfigBase,
    IBaseCRUModalResultBase,
    ICRUModalDialogParameter
} from '../ibase-cru-modal/ibase-cru-modal.component';
import {ETableColDataType, ETableColFilterType, ETableColType, TableColDef} from '../generic-table/table-col-def';
import {PrimeNgImportsModule} from '../../prime-ng-imports';
import {IBaseCRUDContentComponent as IBaseCRUDContentComponent} from '../ibase-crud-content/ibase-crud-content.component';
import {SdkBridge} from '../../../../src/app/sdk-bridge';
import {EDeleteMode} from '../../enums/delete-mode.enum';
import {ReenforcedConfirmModalComponent} from '../../modals/reenforced-confirm-modal/reenforced-confirm-modal.component';
import {MenuItem, TreeNode} from 'primeng/api';
import {ETreeColDataType, ETreeColType, TreeColDef} from '../generic-tree/tree-col-def';
import {StringUtils} from '../../utils/string.util';
import {SDKQueryPaginatorConfig} from '../../services/query-paginator.service';

export class IBaseCRUDConfig<t_Model, t_ModalType, t_ModalExtendedConfig, t_DeletePayload = unknown> {
    /**
     * Configuration of IBaseCRUDComponent
     * @param modelName Name of the model
     * @param getModelHumanIdentifier Model instance human identifier getter
     * @param deleteMode Model deletion mode
     * @param deleteModelService Model deletion service
     * @param modalType Type of the modal to create/read/update a model instance
     * @param getDialogServiceParameters getDialogServiceParameters of the modal
     * @param modalExtendedConfigBuilder Extended configuration builder for the modal
     * @param authorizationNew
     * @param authorizationEdit
     * @param authorizationDelete
     * @param commonAuthorizationNew
     * @param commonAuthorizationEdit
     * @param commonAuthorizationDelete
     */
    constructor(
        public modelName: string,
        public getModelHumanIdentifier: (model: t_Model) => string,
        public deleteMode: EDeleteMode,
        public deleteModelService: ((model: t_Model, payload?: t_DeletePayload) => Observable<boolean>) | undefined,
        public modalType: Type<t_ModalType> | null,
        public getDialogServiceParameters:
            | ((
                  options: ICRUModalDialogParameter<t_ModalExtendedConfig>
              ) => DynamicDialogConfig<IBaseCRUModalConfigBase<t_ModalExtendedConfig>>)
            | null,
        public modalExtendedConfigBuilder: ((model?: t_Model) => t_ModalExtendedConfig | null) | null,
        public authorizationNew: string | null,
        public authorizationEdit: string | null,
        public authorizationDelete: string | null,
        public commonAuthorizationNew?: string,
        public commonAuthorizationEdit?: string,
        public commonAuthorizationDelete?: string
    ) {}
}

/**
 * Base CRUD page component
 */
@Component({
    selector: 'app-ibase-crud',
    template: '',
    imports: [PrimeNgImportsModule]
})
export abstract class IBaseCRUDComponent<
    t_Model,
    t_ModalType,
    t_ModalExtendedConfig,
    t_ModalExtendedResult,
    t_DeletePayload = unknown
> extends IBasePageComponent {
    @ViewChild(IBaseCRUDContentComponent, {static: true})
    protected iBaseCRUDViewBaseContent!: IBaseCRUDContentComponent;

    protected getModelHumanIdentifier!: (model: t_Model) => string;

    protected deleteMode: EDeleteMode = EDeleteMode.CONFIRM;
    protected deleteItemService!: ((model: t_Model, payload?: t_DeletePayload) => Observable<boolean>) | undefined;

    protected getDialogServiceParameters!:
        | ((
              options: ICRUModalDialogParameter<t_ModalExtendedConfig>
          ) => DynamicDialogConfig<IBaseCRUModalConfigBase<t_ModalExtendedConfig>>)
        | null;
    protected modalExtendedConfigBuilder!: ((model?: t_Model) => t_ModalExtendedConfig | null) | null;

    public get paginatorConfig(): SDKQueryPaginatorConfig | undefined {
        return this.iBaseCRUDViewBaseContent?.paginatorConfig;
    }

    public modalType: Type<t_ModalType> | null = null;
    public modelName!: string;
    public modelHumanName!: string;
    public isCommonResource!: boolean;
    public isFromGlobalView!: boolean;
    public globalViewModelNamePrefix = 'Common';
    public tableCols: TableColDef[] = [];
    public treeCols: TreeColDef[] = [];
    public items: t_Model[] = [];
    public treeItems: TreeNode<t_Model>[] = [];
    public totalItemCount = -1;
    public selectedTreeItems?: TreeNode<t_Model> | TreeNode<t_Model>[];

    public authorizationNew: string | null = null;
    public authorizationEdit: string | null = null;
    public authorizationDelete: string | null = null;
    public commonAuthorizationNew?: string;
    public commonAuthorizationEdit?: string;
    public commonAuthorizationDelete?: string;
    public isNewAllowed = false;
    public isEditAllowed = false;
    public isDeleteAllowed = false;
    public shouldUseCommonAuthorizations!: boolean;

    public showCloneBtn = false;
    public contextMenu: MenuItem[] = [{separator: true}];

    public onModalResult: EventEmitter<IBaseCRUModalResultBase<t_ModalExtendedResult>> = new EventEmitter<
        IBaseCRUModalResultBase<t_ModalExtendedResult>
    >();
    public onDeleteResult: EventEmitter<{model: t_Model; payload?: t_DeletePayload}> = new EventEmitter<{
        model: t_Model;
        payload?: t_DeletePayload;
    }>();

    // ===============================================================
    // ===============================================================

    /**
     * Configure the view
     */
    protected configure(config: IBaseCRUDConfig<t_Model, t_ModalType, t_ModalExtendedConfig, t_DeletePayload>) {
        this.modelName = config.modelName;
        this.modelHumanName = StringUtils.toHumanFormatedStr(this.modelName.replace('ApiModel', ''));

        this.getModelHumanIdentifier = config.getModelHumanIdentifier;

        this.deleteMode = config.deleteMode;
        this.deleteItemService = config.deleteModelService;

        this.modalType = config.modalType;
        this.getDialogServiceParameters = config.getDialogServiceParameters;
        this.modalExtendedConfigBuilder = config.modalExtendedConfigBuilder;

        this.authorizationNew = config.authorizationNew;
        this.authorizationEdit = config.authorizationEdit;
        this.authorizationDelete = config.authorizationDelete;
        this.commonAuthorizationNew = config.commonAuthorizationNew;
        this.commonAuthorizationEdit = config.commonAuthorizationEdit;
        this.commonAuthorizationDelete = config.commonAuthorizationDelete;
    }

    // ===============================================================
    // ===============================================================

    /**
     * On init
     */
    public override async ngOnInit() {
        if (!this.modelName) {
            this.globalsService.messageService.add({
                key: this.overrideMessagesKey,
                severity: 'error',
                detail: 'Error, this component is not configured!'
            });

            return;
        }

        this.showLoading = true;

        await SdkBridge.isInMultiSchema();

        if (this.commonAuthorizationNew) {
            this.isCommonResource = true;
            this.isFromGlobalView = true;

            if (await SdkBridge.isInMultiSchema()) this.isFromGlobalView = false;
        } else {
            this.isCommonResource = false;
            this.isFromGlobalView = false;
        }

        this.shouldUseCommonAuthorizations = this.isCommonResource && !this.isFromGlobalView;
        if (this.iBaseCRUDViewBaseContent) this.iBaseCRUDViewBaseContent.iBaseCRUD = this;

        this.isNewAllowed = SdkBridge.isAuthorizedForRouteResourceCreate(
            this.shouldUseCommonAuthorizations ? this.commonAuthorizationNew! : this.authorizationNew!
        );
        this.isEditAllowed = SdkBridge.isAuthorizedForRouteResourceUpdate(
            this.shouldUseCommonAuthorizations ? this.commonAuthorizationEdit! : this.authorizationEdit!
        );
        this.isDeleteAllowed = SdkBridge.isAuthorizedForRouteResourceDelete(
            this.shouldUseCommonAuthorizations ? this.commonAuthorizationDelete! : this.authorizationDelete!
        );

        this.loadingStr = 'Initializing...';

        try {
            await this.initColumns();
            await this.onReady();
            await this.refresh();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            this.globalsService.messageService.add({
                key: this.overrideMessagesKey,
                severity: 'error',
                detail: 'Error while initializing components: ' + (err?.error?.message ?? err.toString())
            });

            // Log error in the console for debugging, do not remove
            console.log(err);

            this.showError = true;
        }

        this.showLoading = false;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Refresh
     */
    public async refresh(): Promise<void> {
        this.showLoading = true;
        this.showError = false;

        try {
            await this.onRefresh();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            this.globalsService.messageService.add({
                key: this.overrideMessagesKey,
                severity: 'error',
                detail: 'Error while refreshing: ' + (err?.error?.message ?? err.toString())
            });

            // Log error in the console for debugging, do not remove
            console.log(err);

            this.showError = true;
        }

        this.showLoading = false;
    }
    /**
     * On refresh (Error handling is done in the parent)
     */
    protected abstract onRefresh(): Promise<void>;

    // ===============================================================
    // ===============================================================

    /**
     * On help button pressed
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public async onHelp(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On new t_Model
     */
    public onNew() {
        if (!this.modalType || !this.getDialogServiceParameters || !this.modalExtendedConfigBuilder) return;

        this.handleDialogRef(
            this.globalsService.dialogService.open(
                this.modalType,
                this.getDialogServiceParameters({
                    dialogOpenMode: EDialogOpenMode.NEW,
                    extended: this.modalExtendedConfigBuilder(undefined)
                })
            )
        );
    }
    /**
     * On clone t_Model
     */
    public onClone(itemToClone: t_Model) {
        if (!this.modalType || !this.getDialogServiceParameters || !this.modalExtendedConfigBuilder) return;

        this.handleDialogRef(
            this.globalsService.dialogService.open(
                this.modalType,
                this.getDialogServiceParameters({
                    dialogOpenMode: EDialogOpenMode.CLONE,
                    extended: this.modalExtendedConfigBuilder(itemToClone)
                })
            )
        );
    }
    /**
     * On edit t_Model
     * @param itemToEdit t_Model to edit
     * @param readonly Read only?
     */
    public onEdit(itemToEdit: t_Model, readonly = false) {
        if (!this.modalType || !this.getDialogServiceParameters || !this.modalExtendedConfigBuilder) return;

        this.handleDialogRef(
            this.globalsService.dialogService.open(
                this.modalType,
                this.getDialogServiceParameters({
                    dialogOpenMode: readonly ? EDialogOpenMode.READ_ONLY : EDialogOpenMode.EDIT,
                    extended: this.modalExtendedConfigBuilder(itemToEdit)
                })
            )
        );
    }

    /**
     * On t_Model delete
     * @param itemToDelete t_Model to delete
     * @param payload Optional payload to send with delete request
     */
    public async onDelete(itemToDelete: t_Model, payload?: t_DeletePayload) {
        if (this.deleteMode !== EDeleteMode.DIRECT) {
            switch (this.deleteMode) {
                case EDeleteMode.CONFIRM:
                    this.globalsService.confirmationService.confirm({
                        key: 'deletion-confirm',
                        header: 'Deletion confirmation',
                        message: `Are you sure that you want to delete'${this.getModelHumanIdentifier(itemToDelete)}'?`,
                        accept: () => {
                            this.callDelete(itemToDelete, true, payload);
                        }
                    });
                    break;

                case EDeleteMode.CONFIRM_REENFORCED:
                    this.globalsService.dialogService
                        .open(
                            ReenforcedConfirmModalComponent,
                            ReenforcedConfirmModalComponent.getDialogServiceParameters({
                                isCreate: false,
                                message: `Are you sure that you want to delete '${this.getModelHumanIdentifier(itemToDelete)}'?`,
                                confirmation: this.getModelHumanIdentifier(itemToDelete)
                            })
                        )
                        .onClose.subscribe((res: boolean) => {
                            if (res) {
                                this.callDelete(itemToDelete, true, payload);
                            }
                        });
                    break;
            }

            return;
        }

        this.callDelete(itemToDelete, true, payload);
    }

    /**
     * trigger delete service process
     * @param itemToDelete t_Model to delete
     * @param autoRefresh Automatically refresh after deletion
     * @param payload Optional payload to send with delete request
     * @returns true when deletion succed or false when failed
     */
    public async callDelete(itemToDelete: t_Model, autoRefresh = true, payload?: t_DeletePayload) {
        this.showLoading = true;
        this.showError = false;

        try {
            if (this.deleteItemService) {
                await firstValueFrom(this.deleteItemService(itemToDelete, payload));
            }

            this.globalsService.messageService.add({
                key: this.overrideMessagesKey,
                severity: 'success',
                summary: 'Deleting ' + this.modelHumanName,
                detail: `Successfully deleted the ${this.modelHumanName} "${this.getModelHumanIdentifier(itemToDelete)}".`
            });

            this.onDeleteResult.emit({model: itemToDelete, payload});

            if (autoRefresh) await this.refresh();

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            this.showError = true;
            this.globalsService.messageService.add({
                key: this.overrideMessagesKey,
                severity: 'error',
                summary: `Deleting ${this.modelHumanName} "${this.getModelHumanIdentifier(itemToDelete)}"  error`,
                detail: err?.error?.message ?? 'Unknown'
            });
        }

        this.showLoading = false;
        return !this.showError;
    }
    /**
     * When the tree selection changed
     * @param selection New selection
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
    public onTreeSelectionChanged(selection: TreeNode<unknown> | TreeNode<unknown>[] | null) {}
    /**
     * When the context menu is shown on a node
     * @param row Context row
     */
    public onContextMenuSelect(row: t_Model) {
        this.contextMenu = this.buildDefaultContextMenu(row);
    }

    // ===============================================================
    // ===============================================================

    /**
     * Handle t_ModalType result
     * @param dialogRef Dialog reference from t_ModalType
     */
    protected handleDialogRef(dialogRef: DynamicDialogRef<t_ModalType>) {
        dialogRef.onClose.subscribe(async (promise: Promise<IBaseCRUModalResultBase<t_ModalExtendedResult>>) => {
            if (promise == undefined) {
                return;
            }

            this.showLoading = true;
            const res = await promise;
            this.showLoading = false;

            if (
                res.dialogOpenMode === EDialogOpenMode.NEW ||
                res.dialogOpenMode == EDialogOpenMode.EDIT ||
                res.dialogOpenMode == EDialogOpenMode.CLONE
            ) {
                this.refresh();
            }

            this.onModalResult.emit(res);
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * Init the columns (Error handling is done in the parent)
     */
    protected abstract initColumns(): Promise<void>;
    /**
     * Add created_at, created_by_name columns
     * @param createdBy Include created by column?
     */
    protected addCreatedColumns(createdBy = true) {
        if (!this.tableCols.find((col) => col.field === 'created_at' && col.dataType == ETableColDataType.DATE)) {
            this.tableCols.push({
                field: 'created_at',
                header: 'Created At',
                searcheable: false,
                filterable: false,
                filterType: ETableColFilterType.DATE,
                dataType: ETableColDataType.DATE,
                colWidth: '10rem'
            });
        }

        if (
            createdBy &&
            !this.tableCols.some((col) => col.field === 'created_by_uuid' && col.dataType == ETableColDataType.TEXT)
        ) {
            this.tableCols.push({
                field: 'created_by_uuid',
                header: 'Created By',
                dataType: ETableColDataType.DATA_DISPLAY_CELL_COMPONENT,
                dataCellDisplayComponent: SdkBridge.userDataCellDisplayComponent,
                searcheable: false,
                filterable: false,
                colWidth: '15rem'
            });
        }
    }
    /**
     * Add the columns with the regular View, Edit, Clone, Delete buttons
     */
    protected addRegularButtonsColumns() {
        const isTree = this.iBaseCRUDViewBaseContent?.mode == 'tree';
        const dst = isTree ? this.treeCols : this.tableCols;

        if (this.modalType && !dst.find((col) => col.header === '___CRUD_VIEW_BTN___')) {
            if (isTree) {
                this.treeCols.push({
                    type: ETreeColType.BUTTON,
                    field: '___CRUD_VIEW_BTN___',
                    header: '',
                    icon: 'pi pi-eye',
                    buttonSeverity: 'secondary',
                    fnOnButtonClick: (row: t_Model) => {
                        this.onEdit(row, true);
                    }
                });
            } else {
                this.tableCols.push({
                    type: ETableColType.BUTTON,
                    field: '___CRUD_VIEW_BTN___',
                    header: '',
                    icon: 'pi pi-eye',
                    buttonSeverity: 'secondary',
                    fnOnButtonClick: (row: t_Model) => {
                        this.onEdit(row, true);
                    }
                });
            }
        }

        const editAutho =
            this.commonAuthorizationEdit && this.isFromGlobalView
                ? this.commonAuthorizationEdit
                : this.authorizationEdit;
        if (
            this.modalType &&
            editAutho &&
            !this.tableCols.find((col) => col.header === '___CRUD_EDIT_BTN___') &&
            SdkBridge.isAuthorizedForRouteResourceUpdate(editAutho)
        ) {
            if (isTree) {
                this.treeCols.push({
                    type: ETreeColType.BUTTON,
                    field: '___CRUD_EDIT_BTN___',
                    header: '',
                    icon: 'pi pi-pencil',
                    fnOnButtonClick: (row: t_Model) => {
                        this.onEdit(row, false);
                    }
                });
                this.treeCols.push({
                    type: ETreeColType.BUTTON,
                    field: '___CRUD_CLONE_BTN___',
                    header: '',
                    icon: 'pi pi-copy',
                    fnOnButtonClick: (row: t_Model) => {
                        this.onClone(row);
                    }
                });
            } else {
                this.tableCols.push({
                    type: ETableColType.BUTTON || ETreeColType.BUTTON,
                    field: '___CRUD_EDIT_BTN___',
                    header: '',
                    icon: 'pi pi-pencil',
                    fnOnButtonClick: (row: t_Model) => {
                        this.onEdit(row, false);
                    }
                });
                this.tableCols.push({
                    type: ETableColType.BUTTON,
                    field: '___CRUD_CLONE_BTN___',
                    header: '',
                    icon: 'pi pi-copy',
                    fnOnButtonClick: (row: t_Model) => {
                        this.onClone(row);
                    }
                });
            }
        }

        const delAutho =
            this.commonAuthorizationDelete && this.isFromGlobalView
                ? this.commonAuthorizationDelete
                : this.authorizationDelete;
        if (
            delAutho &&
            !this.tableCols.find((col) => col.header === '___CRUD_DELETE_BTN___') &&
            SdkBridge.isAuthorizedForRouteResourceDelete(delAutho)
        ) {
            if (isTree) {
                this.treeCols.push({
                    type: ETreeColType.BUTTON,
                    field: '___CRUD_DELETE_BTN___',
                    header: '',
                    icon: 'pi pi-trash',
                    buttonSeverity: 'danger',
                    fnOnButtonClick: (row: t_Model) => {
                        this.onDelete(row);
                    }
                });
            } else {
                this.tableCols.push({
                    type: ETableColType.BUTTON,
                    field: '___CRUD_DELETE_BTN___',
                    header: '',
                    icon: 'pi pi-trash',
                    buttonSeverity: 'danger',
                    fnOnButtonClick: (row: t_Model) => {
                        this.onDelete(row);
                    }
                });
            }
        }
    }
    /**
     * Add the columns for shared models (public / org) that will show the IsCommon and the view button
     */
    protected addSharedModelContextualColumns() {
        const isTree = this.iBaseCRUDViewBaseContent?.mode == 'tree';
        const dst = isTree ? this.treeCols : this.tableCols;

        if (
            this.isFromGlobalView ||
            dst.find((col) => col.field === SdkBridge.multiSchemaModelIdentifierField && col.header == 'Is Common')
        ) {
            return;
        }

        if (isTree) {
            this.treeCols.push({
                field: SdkBridge.multiSchemaModelIdentifierField,
                dataType: ETreeColDataType.BOOLEAN_ICON_INV,
                header: 'Is Common',
                searcheable: false,
                filterable: false
            });
        } else {
            this.tableCols.push({
                field: SdkBridge.multiSchemaModelIdentifierField,
                dataType: ETableColDataType.BOOLEAN_ICON_INV,
                header: 'Is Common',
                searcheable: false,
                filterable: false
            });
        }
    }
    /**
     * Add access button to the columns
     * @param callback Callback function to execute when the button is clicked
     */
    protected addAccessButtonColumn(callback: (row: t_Model) => void, icon = 'pi pi-external-link') {
        const isTree = this.iBaseCRUDViewBaseContent?.mode == 'tree';
        const dst = isTree ? this.treeCols : this.tableCols;
        const header = '___CRUD_ACCESS_BTN___' + icon.replaceAll(' ', '-');

        if (dst.some((col) => col.header == header)) {
            return;
        }

        if (isTree) {
            this.treeCols.push({
                type: ETreeColType.BUTTON,
                field: header,
                header: '',
                icon: icon,
                buttonSeverity: 'secondary',
                fnOnButtonClick: (row: t_Model) => {
                    callback(row);
                }
            });
        } else {
            this.tableCols.push({
                type: ETableColType.BUTTON,
                field: header,
                header: '',
                icon: icon,
                buttonSeverity: 'secondary',
                fnOnButtonClick: (row: t_Model) => {
                    callback(row);
                }
            });
        }
    }
    /**
     * Add generic communication buttons
     * TODO : DISABLED UNTIL DEVELOPPED
     */
    protected addCommunicationButtons() {
        /*
        const isTree = this.iBaseCRUDViewBaseContent?.mode == 'tree';
        const dst = isTree ? this.treeCols : this.tableCols;

        if (!dst.some((col) => col.header == '___CHAT_BTN___')) {
            this.treeCols.push({
                type: ETreeColType.BUTTON,
                field: '___CHAT_BTN___',
                header: '',
                icon: 'pi pi-envelope',
                buttonSeverity: 'secondary'
                //To implement : fnOnButtonClick & call onChat
            });
            this.tableCols.push({
                type: ETableColType.BUTTON,
                field: '___CHAT_BTN___',
                header: '',
                icon: 'pi pi-envelope',
                buttonSeverity: 'secondary'
                //To implement : fnOnButtonClick & call onChat
            });
        }
        if (!dst.some((col) => col.header == '___INSTRUCTION_BTN___')) {
            this.treeCols.push({
                type: ETreeColType.BUTTON,
                field: '___INSTRUCTION_BTN___',
                header: '',
                icon: 'pi pi-clipboard',
                buttonSeverity: 'secondary'
                //To implement : fnOnButtonClick & call onInstruction
            });
            this.tableCols.push({
                type: ETableColType.BUTTON,
                field: '___INSTRUCTION_BTN___',
                header: '',
                icon: 'pi pi-clipboard',
                buttonSeverity: 'secondary'
                //To implement : fnOnButtonClick & call onInstruction
            });
        }*/
    }

    // ===============================================================
    // ===============================================================

    protected buildDefaultContextMenu(row: t_Model): MenuItem[] {
        return [
            {
                label: 'View',
                icon: 'pi pi-eye',
                command: () => {
                    this.onEdit(row, true);
                }
            },
            {
                label: 'Edit',
                icon: 'pi pi-pencil',
                disabled: !this.isEditAllowed,
                command: () => {
                    this.onEdit(row, false);
                }
            },
            {
                label: 'Clone',
                icon: 'pi pi-copy',
                disabled: !this.isNewAllowed,
                command: () => {
                    this.onClone(row);
                }
            },
            {
                label: 'Delete',
                icon: 'pi pi-trash',
                disabled: !this.isDeleteAllowed,
                command: () => {
                    this.onDelete(row);
                }
            }
        ];
    }
}
